import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { 
 generateLeagueStandings,
 generateSwissStandings,
 getRoundName,
 getTournamentProgress
} from "./tournamentUtils";

function TournamentResults() {
 const { tournamentId } = useParams();
 const navigate = useNavigate();
 
 const [tournament, setTournament] = useState(null);
 const [bracket, setBracket] = useState(null);
 const [standings, setStandings] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");
 const [activeTab, setActiveTab] = useState("bracket");

 useEffect(() => {
   fetchTournamentResults();
 }, [tournamentId]);

 const fetchTournamentResults = async () => {
   try {
     const docRef = doc(db, "tournaments", tournamentId);
     const docSnap = await getDoc(docRef);
     
     if (docSnap.exists()) {
       const tournamentData = { id: docSnap.id, ...docSnap.data() };
       setTournament(tournamentData);
       
       if (tournamentData.bracket) {
         setBracket(tournamentData.bracket);
         
         // 순위표 생성
         if (tournamentData.bracket.type === 'league') {
           const leagueStandings = generateLeagueStandings(
             tournamentData.participants || [],
             tournamentData.bracket.matches || []
           );
           setStandings(leagueStandings);
         } else if (tournamentData.bracket.type === 'swiss') {
           const swissStandings = generateSwissStandings(
             tournamentData.participants || []
           );
           setStandings(swissStandings);
         }
       }
     } else {
       setError("대회를 찾을 수 없습니다.");
     }
   } catch (error) {
     console.error("대회 결과 로딩 실패:", error);
     setError("대회 결과를 불러오는데 실패했습니다.");
   } finally {
     setLoading(false);
   }
 };

 const formatDate = (date) => {
   if (!date) return '-';
   const d = date.toDate ? date.toDate() : new Date(date);
   return d.toLocaleDateString('ko-KR');
 };

 const getStatusInfo = (status) => {
   const statusConfig = {
     'registration': { label: '모집중', color: '#10b981', bg: '#d1fae5', icon: '📝' },
     'active': { label: '진행중', color: '#3b82f6', bg: '#dbeafe', icon: '⚡' },
     'completed': { label: '완료', color: '#6b7280', bg: '#f3f4f6', icon: '✅' }
   };
   return statusConfig[status] || statusConfig.registration;
 };

 const getTrophyIcon = (position) => {
   switch (position) {
     case 1: return '🥇';
     case 2: return '🥈';
     case 3: return '🥉';
     default: return '🏅';
   }
 };

 const getWinner = () => {
   if (!bracket) return null;
   
   if (bracket.type === 'tournament' && bracket.rounds) {
     const finalRound = bracket.rounds[bracket.rounds.length - 1];
     const finalMatch = finalRound[0];
     if (finalMatch && finalMatch.status === 'completed') {
       return finalMatch.winner === finalMatch.player1?.userId ? 
              finalMatch.player1 : finalMatch.player2;
     }
   } else if (bracket.type === 'league' && standings.length > 0) {
     return standings[0];
   } else if (bracket.type === 'swiss' && standings.length > 0) {
     return standings[0];
   }
   
   return null;
 };

 if (loading) {
   return (
     <div style={{
       display: 'flex',
       justifyContent: 'center',
       alignItems: 'center',
       minHeight: '60vh',
       fontSize: 18,
       color: '#6b7280'
     }}>
       📊 대회 결과 로딩중...
     </div>
   );
 }

 if (error || !tournament) {
   return (
     <div style={{
       maxWidth: 600,
       margin: '40px auto',
       padding: 40,
       textAlign: 'center',
       background: '#fff',
       borderRadius: 20,
       boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
     }}>
       <div style={{ fontSize: 64, marginBottom: 20 }}>❌</div>
       <h3 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12, color: '#ef4444' }}>
         {error || "대회 결과를 찾을 수 없습니다"}
       </h3>
       <button
         onClick={() => navigate('/tournaments')}
         style={{
           background: '#667eea',
           color: '#fff',
           border: 'none',
           borderRadius: 12,
           padding: '12px 24px',
           fontSize: 16,
           fontWeight: 600,
           cursor: 'pointer'
         }}
       >
         대회 목록으로 돌아가기
       </button>
     </div>
   );
 }

 const statusInfo = getStatusInfo(tournament.status);
 const progress = bracket ? getTournamentProgress(bracket) : 0;
 const winner = getWinner();

 return (
   <div style={{
     maxWidth: 1000,
     margin: '40px auto',
     padding: '0 20px'
   }}>
     {/* 헤더 */}
     <div style={{
       background: `linear-gradient(135deg, ${statusInfo.color} 0%, ${statusInfo.color}dd 100%)`,
       borderRadius: 24,
       padding: '40px 32px',
       color: '#fff',
       marginBottom: 32,
       boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
     }}>
       <div style={{
         display: 'flex',
         justifyContent: 'space-between',
         alignItems: 'flex-start',
         marginBottom: 20
       }}>
         <div>
           <h1 style={{
             fontSize: 36,
             fontWeight: 900,
             margin: 0,
             marginBottom: 8,
             textShadow: '0 2px 4px rgba(0,0,0,0.3)'
           }}>
             📊 {tournament.name}
           </h1>
           <div style={{
             fontSize: 18,
             marginBottom: 12,
             opacity: 0.9
           }}>
             {tournament.sport} • {tournament.type} • {tournament.organizerName}
           </div>
         </div>
         
         <div style={{
           background: 'rgba(255,255,255,0.2)',
           padding: '8px 16px',
           borderRadius: 20,
           fontSize: 14,
           fontWeight: 600,
           display: 'flex',
           alignItems: 'center',
           gap: 6
         }}>
           {statusInfo.icon} {statusInfo.label}
         </div>
       </div>

       {/* 대회 요약 정보 */}
       <div style={{
         display: 'grid',
         gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
         gap: 20,
         opacity: 0.9
       }}>
         <div>
           <div style={{ fontSize: 14, marginBottom: 4 }}>참가자</div>
           <div style={{ fontSize: 20, fontWeight: 600 }}>
             {tournament.participants?.length || 0}명
           </div>
         </div>
         <div>
           <div style={{ fontSize: 14, marginBottom: 4 }}>진행률</div>
           <div style={{ fontSize: 20, fontWeight: 600 }}>
             {progress.toFixed(1)}%
           </div>
         </div>
         <div>
           <div style={{ fontSize: 14, marginBottom: 4 }}>대회 기간</div>
           <div style={{ fontSize: 20, fontWeight: 600 }}>
             {formatDate(tournament.startDate)} ~ {formatDate(tournament.endDate)}
           </div>
         </div>
         {winner && (
           <div>
             <div style={{ fontSize: 14, marginBottom: 4 }}>우승자</div>
             <div style={{ 
               fontSize: 20, 
               fontWeight: 600,
               display: 'flex',
               alignItems: 'center',
               gap: 8
             }}>
               🏆 {winner.name}
             </div>
           </div>
         )}
       </div>
     </div>

     {/* 탭 네비게이션 */}
     <div style={{
       background: '#fff',
       borderRadius: 20,
       padding: '8px',
       marginBottom: 24,
       boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
       border: '1px solid #f1f5f9'
     }}>
       <div style={{ display: 'flex', gap: 4 }}>
         {[
           { key: 'bracket', label: '🏆 대진표/결과' },
           ...(bracket && (bracket.type === 'league' || bracket.type === 'swiss') ? 
             [{ key: 'standings', label: '📊 순위표' }] : []),
           { key: 'stats', label: '📈 통계' }
         ].map(tab => (
           <button
             key={tab.key}
             onClick={() => setActiveTab(tab.key)}
             style={{
               flex: 1,
               background: activeTab === tab.key ? '#667eea' : 'transparent',
               color: activeTab === tab.key ? '#fff' : '#6b7280',
               border: 'none',
               borderRadius: 16,
               padding: '12px 20px',
               fontSize: 16,
               fontWeight: 600,
               cursor: 'pointer',
               transition: 'all 0.2s ease'
             }}
           >
             {tab.label}
           </button>
         ))}
       </div>
     </div>

     {/* 탭 콘텐츠 */}
     {activeTab === 'bracket' && (
       <div style={{
         background: '#fff',
         borderRadius: 20,
         padding: 32,
         boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
         border: '1px solid #f1f5f9'
       }}>
         {!bracket ? (
           <div style={{
             textAlign: 'center',
             padding: 40,
             color: '#9ca3af'
           }}>
             <div style={{ fontSize: 48, marginBottom: 16 }}>🏆</div>
             <div>아직 대진표가 생성되지 않았습니다</div>
           </div>
         ) : (
           <div>
             {bracket.type === 'tournament' && bracket.rounds && (
               <div>
                 <h3 style={{
                   fontSize: 20,
                   fontWeight: 700,
                   marginBottom: 24,
                   color: '#1f2937'
                 }}>
                   🏆 토너먼트 대진표
                 </h3>
                 
                 {bracket.rounds.map((round, roundIndex) => (
                   <div key={roundIndex} style={{ marginBottom: 32 }}>
                     <h4 style={{
                       fontSize: 18,
                       fontWeight: 600,
                       marginBottom: 16,
                       color: '#374151'
                     }}>
                       {getRoundName ? getRoundName(roundIndex + 1, bracket.totalRounds) : `${roundIndex + 1}라운드`}
                     </h4>
                     
                     <div style={{
                       display: 'grid',
                       gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                       gap: 16
                     }}>
                       {round.map((match, matchIndex) => (
                         <div
                           key={match.id}
                           style={{
                             background: match.status === 'completed' ? '#f0fdf4' : 
                                        match.status === 'pending' ? '#f8fafc' : '#f9fafb',
                             border: `2px solid ${
                               match.status === 'completed' ? '#22c55e' : 
                               match.status === 'pending' ? '#3b82f6' : '#e5e7eb'
                             }`,
                             borderRadius: 12,
                             padding: 16
                           }}
                         >
                           <div style={{
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'center',
                             marginBottom: 12
                           }}>
                             <span style={{
                               fontSize: 12,
                               fontWeight: 600,
                               color: '#6b7280'
                             }}>
                               경기 {matchIndex + 1}
                             </span>
                             <span style={{
                               fontSize: 12,
                               fontWeight: 600,
                               color: match.status === 'completed' ? '#22c55e' : 
                                     match.status === 'pending' ? '#3b82f6' : '#9ca3af',
                               background: match.status === 'completed' ? '#dcfce7' :
                                          match.status === 'pending' ? '#dbeafe' : '#f3f4f6',
                               padding: '2px 8px',
                               borderRadius: 4
                             }}>
                               {match.status === 'completed' ? '완료' :
                                match.status === 'pending' ? '진행중' :
                                match.status === 'bye' ? '부전승' : '대기'}
                             </span>
                           </div>

                           <div style={{
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'center'
                           }}>
                             <div style={{
                               fontSize: 14,
                               fontWeight: 600,
                               color: match.winner === match.player1?.userId ? '#22c55e' : '#374151',
                               display: 'flex',
                               alignItems: 'center',
                               gap: 4
                             }}>
                               {match.winner === match.player1?.userId && '🏆'}
                               {match.player1?.name || '대기'}
                               {match.scores?.player1 !== null && (
                                 <span style={{ 
                                   marginLeft: 8, 
                                   color: '#6b7280',
                                   fontSize: 12,
                                   background: '#f3f4f6',
                                   padding: '2px 6px',
                                   borderRadius: 4
                                 }}>
                                   {match.scores.player1}
                                 </span>
                               )}
                             </div>
                             
                             <div style={{
                               fontSize: 12,
                               color: '#9ca3af',
                               margin: '0 12px',
                               fontWeight: 600
                             }}>
                               VS
                             </div>
                             
                             <div style={{
                               fontSize: 14,
                               fontWeight: 600,
                               color: match.winner === match.player2?.userId ? '#22c55e' : '#374151',
                               textAlign: 'right',
                               display: 'flex',
                               alignItems: 'center',
                               gap: 4,
                               flexDirection: 'row-reverse'
                             }}>
                               {match.winner === match.player2?.userId && '🏆'}
                               {match.player2?.name || '대기'}
                               {match.scores?.player2 !== null && (
                                 <span style={{ 
                                   marginRight: 8, 
                                   color: '#6b7280',
                                   fontSize: 12,
                                   background: '#f3f4f6',
                                   padding: '2px 6px',
                                   borderRadius: 4
                                 }}>
                                   {match.scores.player2}
                                 </span>
                               )}
                             </div>
                           </div>
                         </div>
                       ))}
                     </div>
                   </div>
                 ))}
               </div>
             )}

             {bracket.type === 'league' && bracket.matches && (
               <div>
                 <h3 style={{
                   fontSize: 20,
                   fontWeight: 700,
                   marginBottom: 24,
                   color: '#1f2937'
                 }}>
                   🔄 리그전 경기 결과
                 </h3>
                 
                 <div style={{
                   display: 'grid',
                   gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                   gap: 16
                 }}>
                   {bracket.matches.map((match, index) => (
                     <div
                       key={match.id}
                       style={{
                         background: match.status === 'completed' ? '#f0fdf4' : '#f8fafc',
                         border: `2px solid ${match.status === 'completed' ? '#22c55e' : '#e5e7eb'}`,
                         borderRadius: 12,
                         padding: 16
                       }}
                     >
                       <div style={{
                         display: 'flex',
                         justifyContent: 'space-between',
                         alignItems: 'center',
                         marginBottom: 12
                       }}>
                         <span style={{
                           fontSize: 12,
                           fontWeight: 600,
                           color: '#6b7280'
                         }}>
                           경기 {index + 1}
                         </span>
                         <span style={{
                           fontSize: 12,
                           fontWeight: 600,
                           color: match.status === 'completed' ? '#22c55e' : '#3b82f6',
                           background: match.status === 'completed' ? '#dcfce7' : '#dbeafe',
                           padding: '2px 8px',
                           borderRadius: 4
                         }}>
                           {match.status === 'completed' ? '완료' : '예정'}
                         </span>
                       </div>

                       <div style={{
                         display: 'flex',
                         justifyContent: 'space-between',
                         alignItems: 'center'
                       }}>
                         <div style={{
                           fontSize: 14,
                           fontWeight: 600,
                           color: match.winner === match.player1?.userId ? '#22c55e' : '#374151',
                           display: 'flex',
                           alignItems: 'center',
                           gap: 4
                         }}>
                           {match.winner === match.player1?.userId && '🏆'}
                           {match.player1?.name}
                           {match.scores?.player1 !== null && (
                             <span style={{ 
                               marginLeft: 8, 
                               color: '#6b7280',
                               fontSize: 12,
                               background: '#f3f4f6',
                               padding: '2px 6px',
                               borderRadius: 4
                             }}>
                               {match.scores.player1}
                             </span>
                           )}
                         </div>
                         
                         <div style={{
                           fontSize: 12,
                           color: '#9ca3af',
                           margin: '0 12px',
                           fontWeight: 600
                         }}>
                           VS
                         </div>
                         
                         <div style={{
                           fontSize: 14,
                           fontWeight: 600,
                           color: match.winner === match.player2?.userId ? '#22c55e' : '#374151',
                           textAlign: 'right',
                           display: 'flex',
                           alignItems: 'center',
                           gap: 4,
                           flexDirection: 'row-reverse'
                         }}>
                           {match.winner === match.player2?.userId && '🏆'}
                           {match.player2?.name}
                           {match.scores?.player2 !== null && (
                             <span style={{ 
                               marginRight: 8, 
                               color: '#6b7280',
                               fontSize: 12,
                               background: '#f3f4f6',
                               padding: '2px 6px',
                               borderRadius: 4
                             }}>
                               {match.scores.player2}
                             </span>
                           )}
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             )}
           </div>
         )}
       </div>
     )}

     {activeTab === 'standings' && standings.length > 0 && (
       <div style={{
         background: '#fff',
         borderRadius: 20,
         padding: 32,
         boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
         border: '1px solid #f1f5f9'
       }}>
         <h3 style={{
           fontSize: 20,
           fontWeight: 700,
           marginBottom: 24,
           color: '#1f2937'
         }}>
           📊 최종 순위표
         </h3>

         <div style={{ overflowX: 'auto' }}>
           <table style={{
             width: '100%',
             borderCollapse: 'collapse',
             fontSize: 14
           }}>
             <thead>
               <tr style={{
                 background: '#f8fafc',
                 borderBottom: '2px solid #e2e8f0'
               }}>
                 <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>순위</th>
                 <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>참가자</th>
                 <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>경기수</th>
                 <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>승</th>
                 <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>패</th>
                 {bracket?.type === 'league' && (
                   <>
                     <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>무</th>
                     <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>승점</th>
                     <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>득실차</th>
                   </>
                 )}
                 {bracket?.type === 'swiss' && (
                   <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>점수</th>
                 )}
               </tr>
             </thead>
             <tbody>
               {standings.map((participant, index) => (
                 <tr key={participant.userId || index} style={{
                   borderBottom: '1px solid #f1f5f9',
                   background: index < 3 ? 
                     (index === 0 ? '#fef3c7' : index === 1 ? '#e5e7eb' : '#fed7aa') : 
                     'transparent'
                 }}>
                   <td style={{ 
                     padding: '16px', 
                     textAlign: 'center',
                     fontSize: 18,
                     fontWeight: 700
                   }}>
                     {getTrophyIcon(index + 1)} {index + 1}
                   </td>
                   <td style={{ 
                     padding: '16px', 
                     fontWeight: 600, 
                     color: '#1f2937'
                   }}>
                     {participant.name}
                   </td>
                   <td style={{ padding: '16px', textAlign: 'center' }}>
                     {participant.played || 0}
                   </td>
                   <td style={{ padding: '16px', textAlign: 'center', color: '#22c55e', fontWeight: 600 }}>
                     {participant.won || 0}
                   </td>
                   <td style={{ padding: '16px', textAlign: 'center', color: '#ef4444', fontWeight: 600 }}>
                     {participant.lost || 0}
                   </td>
                   {bracket?.type === 'league' && (
                     <>
                       <td style={{ padding: '16px', textAlign: 'center', color: '#f59e0b', fontWeight: 600 }}>
                         {participant.drawn || 0}
                       </td>
                       <td style={{ padding: '16px', textAlign: 'center', fontWeight: 700, color: '#3b82f6' }}>
                         {participant.points || 0}
                       </td>
                       <td style={{ 
                         padding: '16px', 
                         textAlign: 'center', 
                         fontWeight: 600,
                         color: (participant.goalDifference || 0) >= 0 ? '#22c55e' : '#ef4444'
                       }}>
                         {participant.goalDifference >= 0 ? '+' : ''}{participant.goalDifference || 0}
                       </td>
                     </>
                   )}
                   {bracket?.type === 'swiss' && (
                     <td style={{ padding: '16px', textAlign: 'center', fontWeight: 700, color: '#3b82f6' }}>
                       {participant.swissPoints || 0}
                     </td>
                   )}
                 </tr>
               ))}
             </tbody>
           </table>
         </div>
       </div>
     )}

     {activeTab === 'stats' && (
       <div style={{
         background: '#fff',
         borderRadius: 20,
         padding: 32,
         boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
         border: '1px solid #f1f5f9'
       }}>
         <h3 style={{
           fontSize: 20,
           fontWeight: 700,
           marginBottom: 24,
           color: '#1f2937'
         }}>
           📈 대회 통계
         </h3>

         <div style={{
           display: 'grid',
           gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
           gap: 24
         }}>
           {/* 기본 통계 */}
           <div style={{
             background: '#f8fafc',
             borderRadius: 16,
             padding: 24,
             border: '1px solid #e2e8f0'
           }}>
             <h4 style={{
               fontSize: 16,
               fontWeight: 600,
               marginBottom: 16,
               color: '#374151'
             }}>
               📊 기본 정보
             </h4>
             <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
               {[
                 { label: '총 참가자', value: `${tournament.participants?.length || 0}명`, icon: '👥' },
                 { label: '대회 방식', value: tournament.type, icon: '🏆' },
                 { label: '진행률', value: `${progress.toFixed(1)}%`, icon: '📈' },
                 { label: '주최자', value: tournament.organizerName, icon: '🏢' }
               ].map((stat, index) => (
                 <div key={index} style={{
                   display: 'flex',
                   justifyContent: 'space-between',
                   alignItems: 'center',
                   padding: '8px 0',
                   borderBottom: index < 3 ? '1px solid #e2e8f0' : 'none'
                 }}>
                   <div style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 8,
                     color: '#6b7280'
                   }}>
                     <span>{stat.icon}</span>
                     <span>{stat.label}</span>
                   </div>
                   <div style={{
                     fontWeight: 600,
                     color: '#1f2937'
                   }}>
                     {stat.value}
                   </div>
                 </div>
               ))}
             </div>
           </div>

           {/* 경기 통계 */}
           {bracket && (
             <div style={{
               background: '#f0f9ff',
               borderRadius: 16,
               padding: 24,
               border: '1px solid #0ea5e9'
             }}>
               <h4 style={{
                 fontSize: 16,
                 fontWeight: 600,
                 marginBottom: 16,
                 color: '#0c4a6e'
               }}>
                 ⚔️ 경기 통계
               </h4>
               <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                 {(() => {
                   let totalMatches = 0;
                   let completedMatches = 0;
                   
                   if (bracket.rounds) {
                     // 토너먼트
                     bracket.rounds.forEach(round => {
                       round.forEach(match => {
                         if (match.status !== 'waiting') {
                           totalMatches++;
                           if (match.status === 'completed') {
                             completedMatches++;
                           }
                         }
                       });
                     });
                   } else if (bracket.matches) {
                     // 리그전/스위스
                     totalMatches = bracket.matches.length;
                     completedMatches = bracket.matches.filter(m => m.status === 'completed').length;
                   }

                   return [
                     { label: '총 경기수', value: `${totalMatches}경기`, icon: '🎯' },
                     { label: '완료 경기', value: `${completedMatches}경기`, icon: '✅' },
                     { label: '남은 경기', value: `${totalMatches - completedMatches}경기`, icon: '⏳' },
                     { label: '완료율', value: `${totalMatches > 0 ? ((completedMatches / totalMatches) * 100).toFixed(1) : 0}%`, icon: '📊' }
                   ].map((stat, index) => (
                     <div key={index} style={{
                       display: 'flex',
                       justifyContent: 'space-between',
                       alignItems: 'center',
                       padding: '8px 0',
                       borderBottom: index < 3 ? '1px solid #0ea5e920' : 'none'
                     }}>
                       <div style={{
                         display: 'flex',
                         alignItems: 'center',
                         gap: 8,
                         color: '#0c4a6e'
                       }}>
                         <span>{stat.icon}</span>
                         <span>{stat.label}</span>
                       </div>
                       <div style={{
                         fontWeight: 600,
                         color: '#0c4a6e'
                       }}>
                         {stat.value}
                       </div>
                     </div>
                   ));
                 })()}
               </div>
             </div>
           )}

           {/* 상금 정보 */}
           {tournament.prizeMoney && (
             <div style={{
               background: '#fef3c7',
               borderRadius: 16,
               padding: 24,
               border: '1px solid #f59e0b'
             }}>
               <h4 style={{
                 fontSize: 16,
                 fontWeight: 600,
                 marginBottom: 16,
                 color: '#92400e'
               }}>
                 💰 상금 정보
               </h4>
               <div style={{
                 fontSize: 14,
                 color: '#78350f',
                 lineHeight: 1.6,
                 whiteSpace: 'pre-wrap'
               }}>
                 {tournament.prizeMoney}
               </div>
             </div>
           )}

           {/* 참가비 정보 */}
           <div style={{
             background: '#f0fdf4',
             borderRadius: 16,
             padding: 24,
             border: '1px solid #22c55e'
           }}>
             <h4 style={{
               fontSize: 16,
               fontWeight: 600,
               marginBottom: 16,
               color: '#166534'
             }}>
               💳 참가비 정보
             </h4>
             <div style={{
               display: 'flex',
               flexDirection: 'column',
               gap: 8
             }}>
               <div style={{
                 fontSize: 20,
                 fontWeight: 700,
                 color: '#166534'
               }}>
                 {tournament.entryFee > 0 ? `${tournament.entryFee.toLocaleString()}원` : '무료'}
               </div>
               {tournament.entryFee > 0 && (
                 <div style={{
                   fontSize: 12,
                   color: '#059669'
                 }}>
                   총 수익: {((tournament.participants?.length || 0) * tournament.entryFee).toLocaleString()}원
                 </div>
               )}
             </div>
           </div>
         </div>

         {/* 우승자 정보 (대회 완료시) */}
         {winner && tournament.status === 'completed' && (
           <div style={{
             background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
             borderRadius: 20,
             padding: 32,
             marginTop: 24,
             textAlign: 'center',
             color: '#fff',
             boxShadow: '0 10px 40px rgba(251, 191, 36, 0.3)'
           }}>
             <div style={{
               fontSize: 48,
               marginBottom: 16
             }}>
               🏆
             </div>
             <h2 style={{
               fontSize: 28,
               fontWeight: 900,
               margin: 0,
               marginBottom: 8,
               textShadow: '0 2px 4px rgba(0,0,0,0.3)'
             }}>
               우승자
             </h2>
             <div style={{
               fontSize: 24,
               fontWeight: 700,
               marginBottom: 8
             }}>
               {winner.name}
             </div>
             <div style={{
               fontSize: 16,
               opacity: 0.9
             }}>
               🎉 축하합니다! 🎉
             </div>
           </div>
         )}
       </div>
     )}

     {/* 하단 네비게이션 */}
     <div style={{
       background: '#fff',
       borderRadius: 20,
       padding: 24,
       marginTop: 32,
       boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
       border: '1px solid #f1f5f9',
       textAlign: 'center'
     }}>
       <div style={{
         display: 'flex',
         gap: 16,
         justifyContent: 'center'
       }}>
         <button
           onClick={() => navigate('/tournaments')}
           style={{
             background: '#f3f4f6',
             color: '#374151',
             border: 'none',
             borderRadius: 12,
             padding: '12px 24px',
             fontSize: 16,
             fontWeight: 600,
             cursor: 'pointer',
             transition: 'all 0.2s ease'
           }}
           onMouseOver={e => e.target.style.background = '#e5e7eb'}
           onMouseOut={e => e.target.style.background = '#f3f4f6'}
         >
           ← 대회 목록으로
         </button>
         
         <button
           onClick={() => navigate(`/tournament/${tournament.id}`)}
           style={{
             background: '#667eea',
             color: '#fff',
             border: 'none',
             borderRadius: 12,
             padding: '12px 24px',
             fontSize: 16,
             fontWeight: 600,
             cursor: 'pointer',
             transition: 'all 0.2s ease'
           }}
           onMouseOver={e => e.target.style.background = '#5a67d8'}
           onMouseOut={e => e.target.style.background = '#667eea'}
         >
           📋 대회 정보 보기
         </button>
       </div>
     </div>
   </div>
 );
}

export default TournamentResults;